import React, { useEffect, useRef, useState } from 'react';
import AsyncSelect from 'react-select/async';
import axios from '../axiosConfig';
const base = '/api/wip/production-entries';
const errorText = e => e.response?.data?.message || e.message || 'Connection failed. Retry the same request.';

export default function WorkerProductionAction({ action, lotId, supervisorId, selectionEnabled, user, onLock }) {
  const key = `worker-action-retry:${user.EmployeeID}:${action}`;
  const [retry, setRetry] = useState(() => { try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { return null; } });
  const [options, setOptions] = useState([]);
  const [process, setProcess] = useState('');
  const [worker, setWorker] = useState(null);
  const [quantity, setQuantity] = useState(null);
  const [check, setCheck] = useState(null);
  const [code, setCode] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const running = useRef(false);
  const timer = useRef(null);
  const input = useRef(null);
  const option = options[Number(process)];
  const context = {lotId, processGroup:option?.processGroup, processName:option?.processName};
  const ready = !!lotId && process !== '' && !!option && (!selectionEnabled || !!supervisorId);
  useEffect(() => { onLock(busy || !!retry); }, [busy, retry, onLock]);
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    let active = true;
    setOptions([]); setProcess(''); setQuantity(null); setCheck(null); setDone(false); setError(''); setNotice('');
    if (lotId) axios.get(`${base}/process-options`, {params:{lotId}}).then(({data}) => {
      if (!active) return;
      setOptions(data.data); if (data.data.length === 1) setProcess('0');
    }).catch(e => { if(active) setError(errorText(e)); });
    return () => { active=false; };
  }, [lotId, action]);
  useEffect(() => {
    let active=true;
    setQuantity(null); setCheck(null); setDone(false); setNotice('');
    if (ready) {
      const path=action==='ISSUE' ? '/issue-context' : '/bundle-reco';
      axios.get(base+path,{params:{lotId,processGroup:option.processGroup,processName:option.processName}})
        .then(({data})=>{if(active){if(action==='ISSUE') setQuantity(data.data.quantity); else setCheck(data.data);}})
        .catch(e=>{if(active)setError(errorText(e));});
    }
    return ()=>{active=false;};
  }, [lotId, option?.processGroup, option?.processName, ready, action]);
  const headers = id => ({'Idempotency-Key':id,'X-Offline-Worker':String(user.EmployeeID),...(selectionEnabled?{'X-Supervisor-Selection':'v1'}:{})});
  async function workers(search) {
    try { const {data}=await axios.get(`${base}/issue-workers`,{params:{search}});return data.data.map(w=>({value:w.code,label:`${w.name} · ${w.code}`})); }
    catch(e){setError(errorText(e));return [];}
  }
  async function write(request) {
    // Persist the exact request before sending so a lost response/reload can be retried safely.
    sessionStorage.setItem(key,JSON.stringify(request)); setRetry(request);
    const {data}=await axios.post(request.url,request.body,{headers:request.headers});
    if(data.success===false || !data.data)throw Error('Server did not confirm this request. Retry.');
    sessionStorage.removeItem(key); setRetry(null);
    if(request.url.endsWith('/scan')) {setCheck(data.data);setNotice(data.message || `${request.body.barcodeValue} received.`);setDone(false);setCode('');}
    else if(request.url.endsWith('/start-work')){setCheck(null);setDone(false);setNotice('All bundles received. Lot is ready for work.');}
    else {setNotice('ISSUE submitted successfully.');setWorker(null);}
  }
  async function run(work) {
    if(running.current)return;
    running.current=true;setBusy(true);setError('');
    try{await work();}catch(e){setError(errorText(e));
      if([400,403,409,410,422].includes(e.response?.status)){sessionStorage.removeItem(key);setRetry(null);}
    }finally{running.current=false;setBusy(false);input.current?.focus();}
  }
  async function issue() {
    if(!ready || !worker || !Number.isInteger(quantity) || quantity<=0)return;
    await run(()=>write({url:base,headers:headers(`web-issue:${crypto.randomUUID()}`),body:{...context,actionType:'ISSUE',issueWorkerCode:worker.value,expectedQuantity:quantity,rejectionQuantity:0,supervisorEmployeeId:supervisorId}}));
  }
  async function scan(raw) {
    clearTimeout(timer.current);
    const barcode=raw.trim().toUpperCase();
    if(!ready || retry || running.current)return;
    if(!/^(BUN|JOS|SHEET)\d+$/.test(barcode)){setError('Scan a complete BUN, JOS or SHEET barcode.');return;}
    await run(async()=>{
      const {data:status}=await axios.get(`${base}/bundle-reco`,{params:context});
      setCheck(status.data);
      if(status.data.receivedBundles?.includes(barcode)){setNotice(`${barcode} already received. No duplicate added.`);setCode('');return;}
      let qty=1;
      if(option.processName.trim().toUpperCase()!=='FOAM CUTTING'){
        const {data}=await axios.get(`${base}/bundles`,{params:{lotId,includeQuantity:'true',source:'reference'}});
        const found=data.data.find(b=>b.batchNo.trim().toUpperCase()===barcode);
        if(!found || !Number.isInteger(found.quantity) || found.quantity<=0)throw Error('Bundle or valid quantity not found in selected lot reference data.');
        qty=found.quantity;
      }
      await write({url:`${base}/bundle-reco/scan`,headers:headers(`web-reco:${crypto.randomUUID()}`),body:{...context,barcodeValue:barcode,labelQuantity:qty,snapshot:status.data.snapshot,supervisorEmployeeId:supervisorId}});
    });
  }
  async function checkDone(){await run(async()=>{const {data}=await axios.get(`${base}/bundle-reco`,{params:context});setCheck(data.data);setDone(true);});}
  return <div className="scan-context" style={{gridColumn:'1/-1'}}>
    <label>Process<select aria-label="Action process" disabled={busy || !!retry} value={process} onChange={e=>setProcess(e.target.value)}>
      <option value="">Select process</option>{options.map((p,i)=><option key={`${p.processGroup}:${p.processName}`} value={i}>{p.processName} · {p.processGroup}</option>)}</select></label>
    {!lotId && <p>Select Lot ID above to begin.</p>}
    {action==='ISSUE' ? <>
      <div className="worker-context-select" style={{padding:0,border:0}}><div className="worker-context-field"><label htmlFor="issue-worker">Issue to worker</label>
        <AsyncSelect inputId="issue-worker" aria-label="Issue to worker" className="scanner-select" classNamePrefix="scanner-select" loadOptions={workers} defaultOptions value={worker} onChange={setWorker} isDisabled={!ready || busy || !!retry} placeholder="Search worker name or code" /></div>
        <div><label>Quantity</label><p>{quantity ?? '—'} PCS</p></div></div>
      <button className="btn btn-primary" disabled={!ready || !worker || !quantity || busy || !!retry} onClick={issue}>Submit ISSUE</button>
    </> : <>
      <form className="bundle-scan-form" style={{marginTop:14}} onSubmit={e=>{e.preventDefault();scan(code);}}>
        <label>Barcode<input aria-label="Reconciliation barcode" ref={input} value={code} disabled={!ready || busy || !!retry} placeholder="Scan next bundle" onChange={e=>{const value=e.target.value;setCode(value);clearTimeout(timer.current);if(/^(BUN|JOS|SHEET)\d+$/i.test(value.trim()))timer.current=setTimeout(()=>scan(value),350);}} /></label>
        <button className="btn btn-primary" disabled={!ready || busy || !!retry || !code.trim()}>Add scan</button></form>
      {check && <><p>{check.receivedCount} / {check.expectedCount} received</p><div className="reco-received">{(check.receivedBundles||[]).map(b=><span className="scan-kind" key={b}>{b}</span>)}</div></>}
      <button className="btn btn-primary" disabled={!ready || busy || !!retry} onClick={checkDone}>Bundle Done</button>
      {done && check && <div role="alert" style={{marginTop:14}}>{check.canStart ? <><strong>All bundles found. Lot is ready for work.</strong><p>Start Work clears this lot/process receiving checklist.</p><button className="btn btn-primary" disabled={busy || !!retry} onClick={()=>run(()=>write({url:`${base}/bundle-reco/start-work`,headers:headers(`web-start:${crypto.randomUUID()}`),body:{...context,confirmStart:true,snapshot:check.snapshot}}))}>Start Work</button></> : <><strong>Work cannot start. Missing bundles:</strong><p>{(check.missingBundles||[]).join(', ') || 'None'}</p>{!!check.unexpectedBundles?.length && <p>Unexpected: {check.unexpectedBundles.join(', ')}</p>}{!!check.duplicateBundles?.length && <p>Duplicates: {check.duplicateBundles.join(', ')}</p>}</>}</div>}
    </>}
    {notice && <p role="status">{notice}</p>}{error && <p className="bundle-error" role="alert">{error}</p>}
    {retry && <div role="alert"><p>Unconfirmed request for {retry.body.lotId} / {retry.body.processName}. Retry before changing details.</p><button className="btn btn-primary" disabled={busy} onClick={()=>run(()=>write(retry))}>Retry same request</button></div>}
  </div>;
}
